import { useEffect, useRef, useState } from 'preact/hooks';
import type { SupabaseClient } from '@supabase/supabase-js';
import { cargarPedidos, guardarObservacion, type Config } from '../datos/pedidos';
import { Chips, Pill } from '../componentes/controles';
import { money, nf } from '../logica/dinero';
import { hoy } from '../logica/fechas';
import { EST, PENDIENTE_DIRECCION, TONO_PAGO, abierto, atrasado, cuenta, fecha, sinDireccion, totalesPedido, venceCotizacion, type Cliente, type Pedido } from '../logica/pedidos';
import { modoOracion, sinTildes } from '../logica/texto';
import type { Ir } from '../rutas';

type Filtro = { id: string; label: string; f: (p: Pedido) => boolean };
const FILTROS: Filtro[] = [
  { id: 'todos', label: 'Todos', f: () => true },
  { id: 'abiertos', label: 'En curso', f: abierto },
  { id: 'atrasados', label: 'Atrasados', f: p => atrasado(p) },
  { id: 'sindir', label: 'Dirección por confirmar', f: sinDireccion },
  { id: 'saldo', label: 'Con saldo', f: p => totalesPedido(p).saldo > 0 },
  { id: 'cotizacion', label: 'Cotizaciones', f: p => p.estado === 'cotizacion' },
  { id: 'enviado', label: 'Enviados', f: p => p.estado === 'enviado' },
  { id: 'sincosto', label: 'Sin costo de envío', f: p => p.estado === 'enviado' && p.costo_envio == null },
  { id: 'anulado', label: 'Anulados', f: p => p.estado === 'anulado' },
];
// "2× Pantaloneta adulto · Toalla": prendas sumadas por producto, en el orden del pedido.
export const resumenLineas = (p: Pedido, cfg: Config) => {
  const n = new Map<string, number>();
  for (const l of p.lineas) n.set(l.sku, (n.get(l.sku) || 0) + l.cantidad);
  return [...n].map(([sku, c]) => (c > 1 ? c + '× ' : '') + cfg.nombreProducto(sku)).join(' · ');
};

// Observaciones: cualquier requerimiento específico del pedido. Se escriben aquí mismo (toca la celda): se guarda al salir del campo o con
// Enter (Mayús+Enter hace un renglón nuevo), y Escape deshace. No abre el pedido.
function Observacion({ sb, p, onGuardada }: { sb: SupabaseClient; p: Pedido; onGuardada: (id: string, notas: string | null, actualizado: string) => void }) {
  const [editando, setEditando] = useState(false), [v, setV] = useState(''), [err, setErr] = useState(''), [enCurso, setEnCurso] = useState(false);
  const cancelado = useRef(false);   // Escape cierra el campo, y al cerrarse el campo se dispara "salir": ese no debe guardar
  const abrir = (e: Event) => { e.stopPropagation(); cancelado.current = false; setV(p.notas || ''); setErr(''); setEditando(true); };
  const guardar = async () => {
    if (cancelado.current) return;
    const nuevo = modoOracion(v);
    if (nuevo === (p.notas || '')) { setEditando(false); return; }
    setEnCurso(true);
    try { const act = await guardarObservacion(sb, p, nuevo); onGuardada(p.id, nuevo || null, act); setEditando(false); setErr(''); }
    catch (e) { setErr(String((e as Error).message || e)); }
    setEnCurso(false);
  };
  return (
    <div class="o-obs" onClick={e => e.stopPropagation()}>
      {editando
        ? <><textarea class="in area" rows={2} maxLength={500} aria-label={`Observaciones de ${p.id}`} value={v} disabled={enCurso} autoFocus
            onInput={e => setV(e.currentTarget.value)} onBlur={guardar}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); } else if (e.key === 'Escape') { cancelado.current = true; setV(p.notas || ''); setErr(''); setEditando(false); } }} />
          {err && <div class="errtxt" role="alert">{err}</div>}</>
        : <button type="button" class={'obs-txt' + (p.notas ? '' : ' vacia')} aria-label={`Observaciones de ${p.id}: ${p.notas || 'sin observaciones'}. Tocar para escribir`} onClick={abrir}>{p.notas || 'Agregar observación'}</button>}
    </div>
  );
}

export function Pedidos({ sb, cfg, ir }: { sb: SupabaseClient; cfg: Config; ir: Ir }) {
  const [datos, setDatos] = useState<{ pedidos: Pedido[]; clientes: Map<string, Cliente> } | null>(null);
  const [error, setError] = useState('');
  const [f, setF] = useState('todos');
  const [q, setQ] = useState('');
  const alGuardar = (id: string, notas: string | null, actualizado: string) => setDatos(d => d && ({ ...d, pedidos: d.pedidos.map(x => (x.id === id ? { ...x, notas, actualizado } : x)) }));
  useEffect(() => { cargarPedidos(sb).then(setDatos).catch(e => setError(String(e.message || e))); }, []);
  if (error) return <section class="panel"><p class="error" role="alert">No se pudieron leer los pedidos: {error}</p></section>;
  if (!datos) return <section class="panel"><p class="hint">Cargando pedidos…</p></section>;

  const { pedidos, clientes } = datos, HOY = hoy();
  const conteos = Object.fromEntries(FILTROS.map(x => [x.id, pedidos.filter(x.f).length]));
  const qq = sinTildes(q), qd = q.replace(/\D/g, '');
  const lista = pedidos.filter(FILTROS.find(x => x.id === f)!.f).filter(p => {
    if (!qq) return true;
    const k = clientes.get(p.cliente_id);
    return sinTildes(p.id).includes(qq) || sinTildes(k?.nombre || '').includes(qq) || (qd.length >= 3 && (k?.whatsapp || '').includes(qd)) || sinTildes(resumenLineas(p, cfg)).includes(qq) || sinTildes(p.notas || '').includes(qq);
  }).sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id.localeCompare(a.id));

  return (
    <div class="pila">
      <div class="cabeza">
        <div>
          <h2>Pedidos y cotizaciones</h2>
          <p class="hint">{pedidos.length} registrados · el número, los totales y el estado de pago se calculan solos</p>
        </div>
        <button class="btn primary" type="button" onClick={() => ir('pedido', 'nuevo')}>+ Nuevo pedido</button>
      </div>
      <input class="in" type="search" placeholder="Buscar por cliente, WhatsApp, número, producto u observación" aria-label="Buscar pedidos" value={q} onInput={e => setQ(e.currentTarget.value)} />
      <Chips opciones={FILTROS.map(x => ({ id: x.id, label: x.label }))} valor={f} onValor={setF} conteos={conteos} etiqueta="Filtrar pedidos" />
      {f === 'anulado' && lista.length > 0 && (
        <p class="hint">Motivos: {Object.entries(lista.reduce<Record<string, number>>((a, p) => { const m = p.motivo_anulacion || 'Sin motivo'; a[m] = (a[m] || 0) + 1; return a; }, {}))
          .sort((a, b) => b[1] - a[1]).map(([m, n]) => `${m} ${n}`).join(' · ')}</p>
      )}
      <section class="panel flush">
        {lista.length ? (
          <div class="olist">
            <div class="orow-cab con-obs" aria-hidden="true"><span>Pedido</span><span>Cliente</span><span class="num">Total</span><span>Estado</span><span>Entrega</span><span>Observaciones</span></div>
            {lista.map(p => {
            const t = totalesPedido(p), k = clientes.get(p.cliente_id), e = EST[p.estado], m = (v: number) => money(v, p.moneda);
            return (
              <div key={p.id} class="orow con-obs" onClick={() => ir('pedido', p.id)}>
                <div class="o-id"><strong><a class="enlace-fila" href={'#/pedido/' + p.id} onClick={e => e.stopPropagation()}>{p.id}</a></strong><span>{fecha(p.fecha)}</span></div>
                <div class="o-cli"><strong>{k?.nombre || '—'}</strong><span>{resumenLineas(p, cfg)}</span></div>
                <div class="o-tot num"><strong>{m(t.total)}</strong><span>{t.saldo > 0 ? 'Debe ' + m(t.saldo) : nf(t.prendas) + (t.prendas === 1 ? ' prenda' : ' prendas')}</span></div>
                <div class="pills"><Pill tono={e.tono}>{e.label}</Pill>{cuenta(p) && <Pill tono={TONO_PAGO[t.pago]}>{t.pago}</Pill>}{sinDireccion(p) && <Pill tono="warn largo">{PENDIENTE_DIRECCION}</Pill>}</div>
                <div class="o-ent">{
                  p.estado === 'cotizacion' ? <span>Vence {fecha(venceCotizacion(p, cfg))}</span>
                  : p.estado === 'enviado' ? <><span>Enviado {fecha(p.enviado_en)}{p.guia ? ' · guía ' + p.guia : ''}</span>{p.costo_envio == null && <Pill tono="warn">Falta el costo del envío</Pill>}</>
                  : p.estado === 'anulado' ? <span>{[p.motivo_anulacion, t.pagado > 0 ? 'conserva ' + m(t.pagado) : ''].filter(Boolean).join(' · ')}</span>
                  : !p.envio_prometido ? <span />
                  : atrasado(p, HOY) ? <Pill tono="bad">Atrasado · {fecha(p.envio_prometido)}</Pill>
                  : <span>Enviar antes del {fecha(p.envio_prometido)}</span>
                }</div>
                <Observacion sb={sb} p={p} onGuardada={alGuardar} />
              </div>
            );
          })}</div>
        ) : <p class="vacio">{pedidos.length ? 'Ningún pedido coincide con la búsqueda o el filtro.' : 'Aún no hay pedidos. Crea el primero con "Nuevo pedido".'}</p>}
      </section>
    </div>
  );
}
